import Constants from "@/constants/Constants";
import type { IGetShiftReportRequest, IUsersByCompanyIdAndRoleIdRequest } from "@/types/type";

export const queryKeyFactory = {
  users: {
    all: () => [Constants.QueryKeys.USERS] as const,
    detail: (id: string | number) => [...queryKeyFactory.users.all(), id] as const,
    byRole: (roleId: string | number) => [...queryKeyFactory.users.all(), 'byRole', roleId] as const,
    byId: (id: string | number) => [...queryKeyFactory.users.all(), 'byId', id] as const,
    byCompanyAndRole: (companyId: string | number, roleIds: number[]) =>
      [...queryKeyFactory.users.all(), 'byCompany', companyId, 'byRole', roleIds] as const,
    loginHistory: (userId: string | number) => 
      [...queryKeyFactory.users.all(), 'loginHistory', userId] as const,
    companyId: (companyId: number | string) =>
      [...queryKeyFactory.users.all(), 'byCompany', companyId] as const,
    companyIdRoleId: (data: IUsersByCompanyIdAndRoleIdRequest) =>
      [...queryKeyFactory.users.all(), 'byCompany', data.company_id, 'byRole', data.role_id] as const,
    companyIdPositionId: (data: { company_id: number | string; position_id: number | string }) =>
      [...queryKeyFactory.users.all(), 'byCompany', data.company_id, 'byPosition', data.position_id] as const,
    leavingCodes: () =>
      [...queryKeyFactory.users.all(), 'leavingCodes'] as const,
  },

  groups: {
    all: () => [Constants.QueryKeys.GROUPS] as const,
    byBranch: (branchId: string | number) => [...queryKeyFactory.groups.all(), 'byBranch', branchId] as const,
    byCompany: (companyId: string | number) => [...queryKeyFactory.groups.all(), 'byCompany', companyId] as const,
  },
  
  shifts: {
    all: () => [Constants.QueryKeys.SHIFTS] as const,
    detail: (id: string | number) => [...queryKeyFactory.shifts.all(), id] as const,
    byUser: (userId: string | number) => [...queryKeyFactory.shifts.all(), 'byUser', userId] as const,
    byCompany: (companyId: string | number) => [...queryKeyFactory.shifts.all(), 'byCompany', companyId] as const,
    report: (data: IGetShiftReportRequest) =>
      [...queryKeyFactory.shifts.all(), 'report', data.company_id, data.start_date, data.end_date, data.user_id, data.branch_id, data.status] as const,
  },

  routes: {
    all: () => [Constants.QueryKeys.PATROL_ROUTES] as const,
    byCompany: (companyId: string | number) => [...queryKeyFactory.routes.all(), 'byCompany', companyId] as const,
  },

  payrollSlips: {
    all: () => [Constants.QueryKeys.PAYROLL_SLIPS] as const,
    byCompany: (companyId: string | number) => [...queryKeyFactory.payrollSlips.all(), 'byCompany', companyId] as const,
  },

  companies: {
    all: () => [Constants.QueryKeys.COMPANIES] as const,
    detail: (id: string | number) => [...queryKeyFactory.companies.all(), 'detail', id] as const,
  },

  branches: {
    all: () => [Constants.QueryKeys.BRANCHES] as const,
    byCompany: (companyId: string | number) => [...queryKeyFactory.branches.all(), 'byCompany', companyId] as const,
  },
  
  shiftTemplates: {
    all: () => [Constants.QueryKeys.SHIFT_TEMPLATES] as const,
  },

  patrols: {
    all: () => [Constants.QueryKeys.PATROLS] as const,
    byCompany: (companyId: string | number) => [...queryKeyFactory.patrols.all(), 'byCompany', companyId] as const,
    detail: (id: string | number) => [...queryKeyFactory.patrols.all(), id] as const, 
    byUser: (userId: string | number) => [...queryKeyFactory.patrols.all(), 'byUser', userId] as const,
    completed: () => [Constants.QueryKeys.COMPLETED_PATROLS] as const,
    completedByCompanyId: (companyId: string | number) => [...queryKeyFactory.patrols.completed(), 'byCompany', companyId] as const,
  },

  projectDocuments: {
    all: () => [Constants.QueryKeys.PROJECT_DOCUMENTS] as const,
    byProject: (projectId: string | number) => 
      [...queryKeyFactory.projectDocuments.all(), projectId] as const,
  },

  myDocuments: {
    all: () => [Constants.QueryKeys.MY_DOCUMENTS] as const,
  },

  userDocuments: {
    byUser: (userId: string | number) => [Constants.QueryKeys.USER_DOCUMENTS, userId] as const,
  },

  forms: {
    all: () => [Constants.QueryKeys.FORMS] as const,
  },

  formsById: {
    all: () => [Constants.QueryKeys.FORMS_BY_ID] as const,
    detail: (formId: string | number) => 
      [Constants.QueryKeys.FORMS_BY_ID, formId] as const,
  },

  submissionsByFormId: {
    all: () => [Constants.QueryKeys.SUBMISSIONS_BY_FORM_ID] as const,
    byForm: (formId: string | number) => 
      [Constants.QueryKeys.SUBMISSIONS_BY_FORM_ID, formId] as const,
    byFormAndCompany: (formId: string | number, companyId: string | number) => 
      [Constants.QueryKeys.SUBMISSIONS_BY_FORM_ID, formId, companyId] as const,
  },

  formDetailBySubmissionId: {
    all: () => [Constants.QueryKeys.FORM_DETAIL_BY_SUBMISSION_ID] as const,
    bySubmission: (submissionId: string | number) => 
      [Constants.QueryKeys.FORM_DETAIL_BY_SUBMISSION_ID, submissionId] as const,
  },

  departments: {
    all: () => [Constants.QueryKeys.DEPARTMENTS] as const,
    byCompany: (companyId: string | number) => [...queryKeyFactory.departments.all(), companyId] as const,
  },

  scorecard: {
    all: () => [Constants.QueryKeys.SCORECARDS] as const,
    logs: () => [...queryKeyFactory.scorecard.all(), 'logs'] as const,
    lockedDays: () => [...queryKeyFactory.scorecard.all(), 'lockedDays'] as const,
  },
};
